import { useViewer } from "@nexxt/app-kit";
import type { PoStatus } from "@nexxt/contracts/procurement";
import { StatusBadge } from "@nexxt/ui";
import { poStatusTone } from "@nexxt/ui/status";
import type { PurchaseOrder } from "../client";
import { m } from "../paraglide/messages.js";

// Badge texts are sentences from the catalog, never enum values (design-system.md §2.2). The state towards Business
// Central is Tomorrow's: the app adds it.

export const poStatusLabel: Record<PoStatus, () => string> = {
  generated: m.po_status_generated,
  approved: m.po_status_approved,
  published: m.po_status_published,
  in_validation: m.po_status_in_validation,
  confirmed: m.po_status_confirmed,
  closed: m.po_status_closed,
  cancelled: m.po_status_cancelled,
};

export function PoStatusBadge({ status }: { status: PoStatus }) {
  return <StatusBadge tone={poStatusTone[status]} label={poStatusLabel[status]()} />;
}

/** Proposals of the supplier that wait for Operations (FR-M5-05): to decide in Backstage, sent in Atelier. */
export function PendingChangesBadge({ po }: { po: Pick<PurchaseOrder, "pendingChangeCount"> }) {
  const label = usePendingChangesText(po.pendingChangeCount);
  if (po.pendingChangeCount === 0) return null;
  return <StatusBadge tone="warning" label={label} />;
}

export function usePendingChangesText(count: number): string {
  const { isInternal } = useViewer();
  return (isInternal ? m.po_pending_changes : m.po_pending_changes_own)({ count });
}

/** The states of a PO side by side: its status, what waits on it, whether it changed. */
export function PurchaseOrderBadges({
  po,
}: {
  po: Pick<PurchaseOrder, "status" | "pendingChangeCount" | "isAmended">;
}) {
  return (
    <>
      <PoStatusBadge status={po.status} />
      <PendingChangesBadge po={po} />
      {po.isAmended && <StatusBadge tone="neutral" label={m.po_amended()} />}
    </>
  );
}
